import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { feature } from "topojson-client";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { ExpeditionStatusBadge } from "@/components/ExpeditionExtras";
import { journeyTerrains } from "@/lib/expeditions";
import { fmtJourneyDate } from "@/lib/content";

const WORLD_URL = "/world-countries.json";
const INDIA_URL = "/india-states.geojson";

const geoStyle = (active) => ({
  default: { fill: active ? "rgba(118,227,154,0.5)" : "rgba(12,50,68,0.07)", stroke: "#0C3244", strokeWidth: 0.3, strokeOpacity: 0.25, outline: "none", transition: "fill 300ms ease" },
  hover: { fill: active ? "rgba(118,227,154,0.7)" : "rgba(12,50,68,0.11)", outline: "none" },
  pressed: { fill: active ? "rgba(118,227,154,0.7)" : "rgba(12,50,68,0.11)", outline: "none" },
});

function ExpeditionPin({ j, onClick, active }) {
  return (
    <Marker coordinates={[j.longitude, j.latitude]} onClick={() => onClick(j)}>
      <g data-testid={`expedition-marker-${j.id}`} style={{ cursor: "pointer" }}>
        <circle r={active ? 12 : 9} className="map-pin-halo" style={{ fill: "rgba(14,61,82,0.28)" }} />
        <circle r={active ? 4.5 : 3.4} style={{ fill: active ? "#0C3244" : "#0E3D52", stroke: "#F7F4EA", strokeWidth: 1.2 }} />
      </g>
    </Marker>
  );
}

function MarkerCard({ j, onClose }) {
  return (
    <motion.div
      data-testid="map-expedition-card"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.25 }}
      className="relative mt-3 w-full sm:mt-0 sm:absolute sm:bottom-3 sm:left-3 sm:w-[340px] z-20 bg-ptf-primary text-ptf-bg shadow-2xl border border-ptf-bg/10 p-5"
    >
      <button type="button" data-testid="map-card-close" onClick={onClose} aria-label="Close" className="absolute top-3 right-3 grid place-items-center h-7 w-7 text-ptf-bg/60 hover:text-ptf-bg">
        <X className="h-4 w-4" />
      </button>
      <p className="text-[0.58rem] uppercase tracking-[0.2em] text-ptf-accent pr-8">{journeyTerrains(j).join(" · ") || "Expedition"}</p>
      <h4 className="mt-1.5 font-display uppercase text-xl tracking-tight leading-tight">{j.title}</h4>
      <p className="mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-ptf-bg/60">
        {[j.location, j.country].filter(Boolean).join(" · ")}
      </p>
      <p className="mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-ptf-bg/60">
        {j.start_date ? `${fmtJourneyDate(j.start_date)}${j.end_date ? ` to ${fmtJourneyDate(j.end_date)}` : ""}` : "Dates to be announced"}
      </p>
      <div className="mt-3"><ExpeditionStatusBadge status={j.expedition_status} /></div>
      <Link to={`/expeditions/${j.id}`} data-testid="map-card-view" className="mt-4 inline-flex items-center gap-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-ptf-accent hover:text-ptf-bg transition-colors">
        View Expedition <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </motion.div>
  );
}

// Markers are generated dynamically from expedition records (latitude/longitude in the CMS).
export default function ExpeditionMap({ expeditions = [], scope = "" }) {
  const [view, setView] = useState(scope === "INDIA" ? "india" : "world");
  const [active, setActive] = useState(null);
  const [worldGeo, setWorldGeo] = useState(null);
  const [indiaGeo, setIndiaGeo] = useState(null);

  useEffect(() => { setView(scope === "INDIA" ? "india" : "world"); }, [scope]);

  useEffect(() => {
    let alive = true;
    fetch(WORLD_URL).then((r) => r.json()).then((topo) => {
      if (!alive) return;
      const geo = feature(topo, topo.objects.countries);
      // India is always rendered from the Foundation's authoritative full-outline GeoJSON below.
      setWorldGeo({ ...geo, features: geo.features.filter((f) => f.properties.name !== "Antarctica" && f.properties.name !== "India") });
    });
    fetch(INDIA_URL).then((r) => r.json()).then((geo) => { if (alive) setIndiaGeo(geo); });
    return () => { alive = false; };
  }, []);

  const pts = useMemo(
    () => expeditions.filter((j) => typeof j.latitude === "number" && typeof j.longitude === "number"),
    [expeditions]
  );
  const indiaPts = useMemo(() => pts.filter((j) => j.country === "India"), [pts]);
  const activeCountries = useMemo(() => new Set(pts.map((j) => j.country).filter(Boolean)), [pts]);

  useEffect(() => {
    if (active && !pts.some((j) => j.id === active.id)) setActive(null);
  }, [pts, active]);

  return (
    <div className="relative w-full" data-testid="expedition-map">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[["world", "World"], ["india", "India"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`map-view-${v}`} onClick={() => { setView(v); setActive(null); }}
            className={`text-[0.62rem] uppercase tracking-[0.14em] px-4 py-2 border transition-colors ${view === v ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}>
            {l}
          </button>
        ))}
        <span className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/45 ml-auto" data-testid="map-marker-count">
          {view === "india"
            ? (indiaPts.length === 0 ? "No India expeditions in this search" : `${indiaPts.length} expedition location${indiaPts.length === 1 ? "" : "s"}`)
            : (pts.length === 0 ? "No expeditions on the map for this search" : `${pts.length} expedition location${pts.length === 1 ? "" : "s"}`)}
        </span>
      </div>

      <div className="relative mx-auto md:max-w-[900px]">
      <div className="relative border border-ptf-primary/12 bg-white overflow-hidden">
        <AnimatePresence mode="wait">
          {view === "world" ? (
            <motion.div key="world" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
              <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 175, center: [12, 8] }} width={960} height={480} style={{ width: "100%", height: "auto" }}>
                <ZoomableGroup zoom={1} minZoom={1} maxZoom={8}>
                  {worldGeo && (
                    <Geographies geography={worldGeo}>
                      {({ geographies }) => geographies.map((geo) => (
                        <Geography key={geo.rsmKey} geography={geo} style={geoStyle(activeCountries.has(geo.properties.name))} />
                      ))}
                    </Geographies>
                  )}
                  {indiaGeo && (
                    <Geographies geography={indiaGeo}>
                      {({ geographies }) => {
                        const st = geoStyle(activeCountries.has("India"));
                        const sealed = {
                          default: { ...st.default, stroke: st.default.fill, strokeWidth: 0.6, strokeOpacity: 1 },
                          hover: { ...st.hover, stroke: st.hover.fill, strokeWidth: 0.6 },
                          pressed: st.pressed,
                        };
                        return geographies.map((geo) => <Geography key={geo.rsmKey} geography={geo} style={sealed} />);
                      }}
                    </Geographies>
                  )}
                  {pts.map((j) => <ExpeditionPin key={j.id} j={j} active={active?.id === j.id} onClick={setActive} />)}
                </ZoomableGroup>
              </ComposableMap>
            </motion.div>
          ) : (
            <motion.div key="india" className="mx-auto md:max-w-[520px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
              <ComposableMap projection="geoMercator" projectionConfig={{ scale: 950, center: [82.8, 22.8] }} width={960} height={620} style={{ width: "100%", height: "auto" }}>
                <ZoomableGroup zoom={1} minZoom={1} maxZoom={8}>
                  {indiaGeo && (
                    <Geographies geography={indiaGeo}>
                      {({ geographies }) => geographies.map((geo) => (
                        <Geography key={geo.rsmKey} geography={geo} style={geoStyle(false)} />
                      ))}
                    </Geographies>
                  )}
                  {indiaPts.map((j) => <ExpeditionPin key={j.id} j={j} active={active?.id === j.id} onClick={setActive} />)}
                </ZoomableGroup>
              </ComposableMap>
              {indiaPts.length === 0 && (
                <p data-testid="map-india-empty" className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm italic font-serif-ed text-ptf-text/55 px-6 pointer-events-none">
                  No India expeditions match this search yet. India journeys will appear here as they are announced.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>{active && <MarkerCard j={active} onClose={() => setActive(null)} />}</AnimatePresence>
      </div>
      </div>
      <p className="mt-2 text-[0.6rem] uppercase tracking-[0.16em] text-ptf-text/45">Scroll or pinch to zoom · drag to pan · tap a marker to open the expedition</p>
    </div>
  );
}
